// externals
import type { FC, PropsWithChildren, MutableRefObject } from 'react';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual, cloneDeep } from 'lodash';
import { nanoid } from 'nanoid';

// libraries
import * as accessoriesActionCreators from '@makemydeal/dr-dash-store/actions/accessoriesActionCreators';
import * as accessoriesActionTypes from '@makemydeal/dr-dash-store/actionTypes/accessoriesActionTypes';
import * as accessoriesSelectors from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { Typography } from '@interstate/components/Typography';
import { Action } from '@interstate/components/Action';
import { Alert } from '@interstate/components/Alert';
import { PlusIcon } from '@interstate/components/Icons';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';

// components
import { AccessoryRowForm } from './AccessoryRowForm.interstate';
import { AccessoriesSummary } from './AccessoriesSummary.interstate';
import { useAccessoryActions } from '../../useAccessoryActions';

// styles
import {
    AccessoriesTotalRow,
    AddAccessoryActionBaseContainer,
    BaseContainer,
    ManualAccessoriesErrorContainer,
    ManualAccessoriesFormBaseContainer
} from '../../ManualAccessories.interstate.styles';

// interfaces/types
import type { Accessory } from '@makemydeal/dr-dash-types';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { BaseDealBreakpoint } from '../../../types';
import { paymentFields } from '../manualAccessoriesConsts';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { REQUIRED_INDICATOR } from '../../../constants';

// mocking accessories until they are available from state
const getInitAccessoryData = (): Accessory => ({
    code: '',
    name: '',
    amount: undefined,
    cost: undefined,
    deleted: false,
    originalAmount: 0,
    profit: 'frontEndProfit',
    residual: 0,
    residualType: 'Dollar',
    upFront: false,
    weOwe: false,
    isManualAccessory: true
});

interface AccessoriesForm {
    baseBreakPoint: BaseDealBreakpoint;
    isInsideSlideOut?: boolean;
    isAccessoriesFormDirtyRef?: MutableRefObject<boolean>;
    shouldTriggerValidation?: boolean;
}

export const AccessoriesForm: FC<PropsWithChildren<AccessoriesForm>> = ({
    baseBreakPoint,
    isInsideSlideOut = false,
    isAccessoriesFormDirtyRef,
    shouldTriggerValidation = false
}) => {
    const dispatch = useDispatch();
    const stateAccessories = useSelector(accessoriesSelectors.getAccessoriesList);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const vin = useSelector(vehicleSelectors.getVehicleVin);
    const hasLocalModificationsRef = useRef(false);
    const {
        hasChanges,
        setHasChanges,
        accessories,
        addAccessory,
        updateAccessory,
        updateAccessoryRow,
        setAccessories,
        getFieldValidation,
        isRowValid,
        imperativeValidate,
        hasErrors,
        hasHiddenErrors,
        formErrors,
        validateField
    } = useAccessoryActions(stateAccessories, hasLocalModificationsRef);

    const [haveAccessoriesAdded, setHaveAccessoriesAdded] = useState(false);
    const [makePaymentCall, setMakePaymentCall] = useState(false);
    const isPaymentError = useSelector(paymentSelectors.getIsError);
    const failedAccessories = useSelector(deskingSelectors.wasTheLastFailure(accessoriesActionTypes.ADD_ACCESSORIES_SELECTIONS));
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const isManualTemplateApplied = useSelector(dealTemplateSelectors.isDealTemplateManuallyApplied);

    const [shouldSaveAccessoryOnUpdate, setShouldSaveAccessoryOnUpdate] = useState<boolean>(false);
    const theme = useInterstateTheme();
    const shouldShowPaymentAlert = haveAccessoriesAdded && isPaymentError && failedAccessories;
    const shouldShowAlertContainer = shouldShowPaymentAlert || hasErrors;
    const isLocked = dealLockHooks.useIsDealLocked();
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);

    useEffect(() => {
        if (isAccessoriesFormDirtyRef) {
            isAccessoriesFormDirtyRef.current = hasChanges;
        }
    }, [hasChanges]);

    useEffect(() => {
        if (isManualTemplateApplied) {
            setAccessories(cloneDeep(stateAccessories));
        }
    }, [isManualTemplateApplied]);

    useEffect(() => {
        if (offerHasBeenSaved) {
            setAccessories(stateAccessories);
        }
    }, [offerHasBeenSaved]);

    useEffect(() => {
        if (shouldSaveAccessoryOnUpdate && !hasHiddenErrors) {
            handleSave({ skipPayment: false });
        }
    }, [hasHiddenErrors, accessories, shouldSaveAccessoryOnUpdate]);

    useEffect(() => {
        if (shouldTriggerValidation) {
            imperativeValidate();
        }
    }, [shouldTriggerValidation, imperativeValidate]);

    useEffect(() => {
        dispatch(accessoriesActionCreators.openForm());
    }, []);

    const handleRecalcModal = (fieldName: string, hasChanged: boolean) => {
        if (!hasErrors && paymentFields.includes(fieldName)) {
            dispatch(offerActionCreators.updatePaymentCallPendingState(hasChanged));
        }
    };
    const handleSoftSave = (fieldName: string, updatedAccessories?: Accessory[]) => {
        const isWeOweField = fieldName === 'weOwe';

        if (isWeOweField) {
            handleSave({ skipPayment: true }, updatedAccessories);
            return;
        }

        if (hasErrors) {
            setMakePaymentCall(true);
            return;
        }

        const skipPayment = !(paymentFields.includes(fieldName) || makePaymentCall);
        handleSave({ skipPayment }, updatedAccessories);
        return;
    };

    const handleRemoveAccessory = useCallback(
        (index: number) => {
            updateAccessory(index, 'deleted', true, true);
            setShouldSaveAccessoryOnUpdate(true);
        },
        [updateAccessory]
    );

    const handleRestoreAccessory = useCallback(
        (index: number) => {
            updateAccessory(index, 'deleted', false, true);
            if (isRowValid(index)) {
                setShouldSaveAccessoryOnUpdate(true);
            }
        },
        [updateAccessory, isRowValid]
    );

    const handleUpdateAccessoryRow = useCallback(
        (index: number, updatedAccessory: Accessory) => {
            updateAccessoryRow(index, updatedAccessory);
            setShouldSaveAccessoryOnUpdate(true);
        },
        [updateAccessoryRow]
    );

    const handleSave = (options: { skipPayment: boolean }, updatedAccessories?: Accessory[]) => {
        if (hasHiddenErrors) {
            return;
        }
        hasLocalModificationsRef.current = true;
        const accessoriesToSave = updatedAccessories || accessories;

        accessoriesToSave.forEach((accessoryToSave, index) => {
            const stateAccessory = stateAccessories?.find((accessoryFromState) => {
                if (accessoryToSave.manualAccessoryId && accessoryFromState.manualAccessoryId) {
                    return accessoryToSave.manualAccessoryId === accessoryFromState.manualAccessoryId;
                }

                if (accessoryToSave.code && accessoryFromState.code) {
                    return accessoryToSave.code === accessoryFromState.code;
                }

                return false;
            });

            if (stateAccessory) {
                const { key: _accKey, manualAccessoryId: _accManualId, ...accComparable } = accessoryToSave;
                const { key: _stateKey, manualAccessoryId: _stateManualId, ...stateComparable } = stateAccessory as any;

                const hasChanges = !isEqual(accComparable, stateComparable);

                if (hasChanges) {
                    accessoryToSave.isManualAccessory = true;
                    if (!accessoryToSave.manualAccessoryId) {
                        accessoryToSave.manualAccessoryId = nanoid();
                    }
                } else {
                    accessoryToSave.isManualAccessory = stateAccessory.isManualAccessory ?? false;
                }
            } else {
                accessoryToSave.isManualAccessory = true;
                if (!accessoryToSave.manualAccessoryId) {
                    accessoryToSave.manualAccessoryId = nanoid();
                }
            }

            // Store original index for accessories
            (accessoryToSave as any).originalIndex = index;
        });
        const accessoriesUpdated = [...accessoriesToSave].filter((acc) => !acc.deleted);

        setHaveAccessoriesAdded(true);
        setHasChanges(false);
        setShouldSaveAccessoryOnUpdate(false);
        setMakePaymentCall(false);

        dispatch(accessoriesActionCreators.addManualAccessories(accessoriesUpdated, options));
        if (isContractingContext) {
            dispatch(contractingActionCreators.refreshContractValidationErrors());
        }
    };

    const addFromCatalogClickHandler = () => {
        if (isInsideSlideOut) {
            dispatch(accessoriesActionCreators.openAccessoriesCatalogSlideOut());
        } else {
            dispatch(accessoriesActionCreators.redirectToAccessoriesCatalogPage());
        }

        logNewRelicPageAction('MV:Accessories - Add From Catalog', {
            offerType,
            dealXgId,
            dealXgVersion,
            dealerId,
            vin
        });
    };

    const addManualAccessoryClickHandler = () => {
        setHaveAccessoriesAdded(false);
        addAccessory(getInitAccessoryData());
        dispatch(interactionAnalyticsActionCreators.addAccessoriesLink());

        logNewRelicPageAction('MV:Accessories - Add Manual Accessory', {
            offerType,
            dealXgId,
            dealXgVersion,
            dealerId,
            vin
        });
    };

    const formTotalAmount = useMemo(() => {
        return accessories.reduce((sum, accessory) => sum + (accessory.deleted ? 0 : accessory.amount || 0), 0);
    }, [accessories]);

    const hasCatalogAccessories = useSelector(accessoriesSelectors.getHasCatalogAccessories);
    const isDealerAccessoriesCatalogEnabled = useSelector(dealerSelectors.isAccessoriesCatalogEnabled);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);

    const disabledAddFromCatalog = !hasCatalogAccessories || isLocked;

    const hasContractingErrors = isContractingContext && hasHiddenErrors;

    const invalidRequiredFieldLabels = useMemo(() => {
        if (!hasErrors) {
            return [];
        }

        const fieldLabelsByName: Record<string, string> = {
            name: 'Name',
            amount: 'Sale',
            cost: 'Cost'
        };

        const invalidFieldNames = new Set<string>();

        Object.values(formErrors).forEach((row) => {
            Object.entries(row || {}).forEach(([fieldName, value]) => {
                if (value?.hasError) {
                    invalidFieldNames.add(fieldName);
                }
            });
        });

        return Array.from(invalidFieldNames).map((fieldName) => fieldLabelsByName[fieldName]);
    }, [formErrors]);

    const invalidRequiredFieldsMessage = useMemo(() => {
        if (!invalidRequiredFieldLabels.length) {
            return 'One or more accessory field(s) have errors. Correct error(s) and save your entries again.';
        }

        if (invalidRequiredFieldLabels.length === 1) {
            return `${invalidRequiredFieldLabels[0]} field has not been filled out. Correct error(s) and save your entries again.`;
        }

        const allButLast = invalidRequiredFieldLabels.join(', ');
        return `${allButLast} field(s) have not been filled out. Correct error(s) and save your entries again.`;
    }, [invalidRequiredFieldLabels]);

    return (
        <>
            {hasContractingErrors && (
                <Alert type="error" sx={{ margin: '1rem 0' }}>
                    Cannot generate contract. Please fix the highlighted accessories details before continuing.
                </Alert>
            )}
            <ManualAccessoriesErrorContainer data-testid="manual-accessories-error-container" opened={shouldShowAlertContainer}>
                {hasErrors && (
                    <Alert
                        data-testid={'manual-accessories-validation-error'}
                        role="manual-accessories-validation-error"
                        type={'error'}
                    >
                        <Typography tag="span" variant="body-sm" data-testid="manual-accessories-validation-error-message">
                            {invalidRequiredFieldsMessage}
                        </Typography>
                    </Alert>
                )}
                {shouldShowPaymentAlert && !hasErrors && (
                    <Alert data-testid={'manual-accessories-payment-error'} role="manual-accessories-payment-error" type={'error'}>
                        <Typography tag="span" variant="body-sm" data-testid="manual-accessories-payment-error-message">
                            Unknown accessories error(s). Check and save your entries again.
                        </Typography>
                    </Alert>
                )}
            </ManualAccessoriesErrorContainer>
            <div>
                {accessories.length > 0 && (
                    <BaseContainer
                        data-testid="base-container"
                        className={offerType === LEASE ? 'lease-layout' : ''}
                        $baseBreakPoint={baseBreakPoint}
                    >
                        <p style={{ gridColumn: 'span 2' }}>Name ({REQUIRED_INDICATOR})</p>
                        <p>ID</p>
                        <p>Sale ({REQUIRED_INDICATOR})</p>
                        <p>Cost ({REQUIRED_INDICATOR})</p>
                        <p>Install</p>
                        {offerType === LEASE && <p>Residual</p>}
                        <p>Profit</p>
                    </BaseContainer>
                )}
            </div>
            {accessories.map((accessory, index: number) => (
                <ManualAccessoriesFormBaseContainer
                    id="manual-accessories-form-container"
                    data-testid="manual-accessories-form-container"
                    key={`manual-accessories-form-container-${accessory.key}`}
                    theme={theme}
                    className={index === 0 ? 'first-accessory-form' : ''}
                    $baseBreakPoint={baseBreakPoint}
                >
                    <AccessoryRowForm
                        index={index}
                        accessory={accessory}
                        accessories={accessories}
                        updateAccessory={updateAccessory}
                        updateAccessoryRow={handleUpdateAccessoryRow}
                        getFieldValidation={getFieldValidation}
                        removeAccessory={handleRemoveAccessory}
                        restoreAccessory={handleRestoreAccessory}
                        isLeaseLayout={offerType === LEASE}
                        handleSoftSave={handleSoftSave}
                        handleRecalcModal={handleRecalcModal}
                        validateField={validateField}
                        shouldTriggerValidation={shouldTriggerValidation}
                        baseBreakPoint={baseBreakPoint}
                    />
                </ManualAccessoriesFormBaseContainer>
            ))}
            <AddAccessoryActionBaseContainer
                theme={theme}
                $baseBreakPoint={baseBreakPoint}
                style={{ borderBottom: '2px solid #b2b2b2' }}
            >
                <Action
                    data-testid="add-accessory-button"
                    disabled={isLocked}
                    onClick={addManualAccessoryClickHandler}
                    actionIcon={{ icon: <PlusIcon />, start: true }}
                >
                    Add Accessory
                </Action>
                {isDealerAccessoriesCatalogEnabled && isDealCentralExperience && (
                    <Action
                        data-testid="accessories-card-catalog-link"
                        disabled={disabledAddFromCatalog}
                        onClick={addFromCatalogClickHandler}
                        sx={{ marginLeft: '2rem' }}
                    >
                        Add From Catalog
                    </Action>
                )}
            </AddAccessoryActionBaseContainer>
            <AccessoriesTotalRow>
                <AccessoriesSummary totalAmount={formTotalAmount} />
            </AccessoriesTotalRow>
        </>
    );
};
